import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { solutionService } from '../services/solutionService';
import type { Solution, FtoFinalPatentEntry } from '../services/subProjectService';

interface FtoScopePaneProps {
  subProjectId: string;
  solutionId: string;
  reloadTrigger: number;
}

const LegalStatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  if (!status) return <span className="text-slate-400">—</span>;
  const colors: Record<string, string> = {
    granted: 'bg-red-100 text-red-700',
    pending: 'bg-yellow-100 text-yellow-700',
    expired: 'bg-slate-100 text-slate-500',
    lapsed: 'bg-slate-100 text-slate-500',
    revoked: 'bg-slate-100 text-slate-500',
    abandoned: 'bg-slate-100 text-slate-500',
    unknown: 'bg-slate-100 text-slate-400',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[status] || colors.unknown}`}>
      {status}
    </span>
  );
};

const ClaimsStatusBadge: React.FC<{ status?: string | null; source?: string | null }> = ({ status, source }) => {
  if (!status) return <span className="text-slate-400">—</span>;
  if (status === 'retrieved') {
    return (
      <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-medium" title={source || ''}>
        retrieved
      </span>
    );
  }
  if (status === 'unavailable') {
    return (
      <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 text-[10px] font-medium">
        unavailable
      </span>
    );
  }
  return (
    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-medium">
      {status}
    </span>
  );
};

const FtoScopePane: React.FC<FtoScopePaneProps> = ({ subProjectId, solutionId, reloadTrigger }) => {
  const { t } = useTranslation();
  const [solution, setSolution] = useState<Solution | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingCountries, setEditingCountries] = useState(false);
  const [countriesInput, setCountriesInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [finalPatents, setFinalPatents] = useState<FtoFinalPatentEntry[]>([]);
  const [expandedClaimsId, setExpandedClaimsId] = useState<string | null>(null);
  const [claimsTexts, setClaimsTexts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ phase: string; current: number; total: number } | null>(null);

  // Poll recompute progress while the operation is in flight
  useEffect(() => {
    if (!recomputing) { setProgress(null); return; }
    const interval = setInterval(async () => {
      try {
        setProgress(await solutionService.getFtoRecomputeProgress(subProjectId, solutionId));
      } catch { /* 404 = no progress yet or phase transition — keep last known */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [recomputing, subProjectId, solutionId]);

  const errorDetail = useCallback((e: unknown, fallback: string): string => {
    const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
    return typeof detail === 'string' ? detail : fallback;
  }, []);

  const load = useCallback(async () => {
    try {
      const sol = await solutionService.get(subProjectId, solutionId);
      setSolution(sol);
      setFinalPatents(sol.fto_final_patents || []);
    } catch {
      setError(t('cfa.fto.loadError', 'Failed to load the FTO scope.'));
    }
    setLoading(false);
  }, [subProjectId, solutionId, t]);

  useEffect(() => { load(); }, [load, reloadTrigger]);

  const handleSaveCountries = async () => {
    const codes = countriesInput.split(/[\s,;]+/).map(s => s.trim().toUpperCase()).filter(Boolean);
    setSaving(true);
    setError(null);
    try {
      await solutionService.patchFtoCountries(subProjectId, solutionId, codes);
      setEditingCountries(false);
      await load();
    } catch (e) {
      setError(errorDetail(e, t('cfa.fto.saveCountriesError', 'Failed to save the country list.')));
    }
    setSaving(false);
  };

  const handleRecompute = async () => {
    setRecomputing(true);
    setError(null);
    try {
      await solutionService.recomputeFtoFamilies(subProjectId, solutionId);
      await load();
    } catch (e) {
      setError(errorDetail(e, t('cfa.fto.recomputeError', 'Recompute failed. EPO services may be unavailable — try again later.')));
    }
    setRecomputing(false);
  };

  const handleToggleClaims = async (patentId: string) => {
    if (expandedClaimsId === patentId) { setExpandedClaimsId(null); return; }
    if (!claimsTexts[patentId]) {
      try {
        const data = await solutionService.getFtoPatentClaims(subProjectId, solutionId, patentId);
        setClaimsTexts(prev => ({ ...prev, [patentId]: data.claims_text }));
      } catch { return; }
    }
    setExpandedClaimsId(patentId);
  };

  if (loading) return <div className="p-3 text-xs text-slate-400">{t('common.loading', 'Loading...')}</div>;

  const countries = solution?.fto_countries || [];
  const initialCount = solution?.fto_initial_patents?.length || 0;
  const finalCount = finalPatents.length;

  const countryGroups = finalPatents.reduce<Record<string, number>>((acc, p) => {
    acc[p.country] = (acc[p.country] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-4 p-4 overflow-auto h-full">
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">{error}</div>
      )}
      {/* Countries */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-slate-600">{t('cfa.fto.countriesLabel', 'Countries')}</span>
          {!editingCountries && (
            <button
              onClick={() => { setCountriesInput(countries.join(', ')); setEditingCountries(true); }}
              className="text-[10px] px-2 py-0.5 rounded border border-slate-300 text-slate-500 hover:border-blue-400 hover:text-blue-600"
            >
              {t('cfa.fto.editCountries', 'Edit')}
            </button>
          )}
        </div>
        {editingCountries ? (
          <div className="flex gap-2 items-start">
            <input
              className="flex-1 text-xs border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-400"
              value={countriesInput}
              onChange={e => setCountriesInput(e.target.value)}
              placeholder="EP, US, JP, CN, DE, KR"
            />
            <button
              onClick={handleSaveCountries}
              disabled={saving}
              className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? '...' : t('common.save', 'Save')}
            </button>
            <button
              onClick={() => setEditingCountries(false)}
              className="text-xs px-2 py-1 border border-slate-300 text-slate-500 rounded hover:bg-slate-50"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {countries.length > 0 ? countries.map(c => (
              <span key={c} className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-medium">{c}</span>
            )) : <span className="text-xs text-slate-400">{t('cfa.fto.noCountries', '(not set)')}</span>}
          </div>
        )}
      </div>

      {/* Counts */}
      <div className="flex gap-6 text-xs text-slate-600">
        <div>
          <span className="font-semibold">{initialCount}</span>
          <span className="ml-1 text-slate-500">{t('cfa.fto.initialPatentsLabel', 'initial patents')}</span>
        </div>
        <div>
          <span className="font-semibold">{finalCount}</span>
          <span className="ml-1 text-slate-500">{t('cfa.fto.finalPatentsLabel', 'final patents (post-families)')}</span>
        </div>
      </div>

      {/* Recompute button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleRecompute}
          disabled={recomputing || initialCount === 0}
          className="text-xs px-3 py-1.5 border border-slate-300 rounded text-slate-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-40"
        >
          {recomputing ? t('common.loading', 'Loading...') : t('cfa.fto.recompute', 'Recompute patent families')}
        </button>
        {recomputing && (
          <span className="flex items-center gap-1.5 text-xs text-blue-600">
            <span className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            {progress
              ? `${t(`cfa.fto.phase.${progress.phase}`, progress.phase)} — ${progress.current}/${progress.total}`
              : t('cfa.fto.recomputeStarting', 'Starting...')}
          </span>
        )}
      </div>

      {/* Final patents breakdown by country */}
      {finalCount > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">{t('cfa.fto.byCountry', 'By country')}</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(countryGroups).map(([country, count]) => (
              <span key={country} className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                {country}: {count}
              </span>
            ))}
          </div>

          {/* Detailed patent table */}
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-2 py-1 text-left font-semibold text-slate-600">Patent</th>
                  <th className="px-2 py-1 text-left font-semibold text-slate-600">Country</th>
                  <th className="px-2 py-1 text-left font-semibold text-slate-600">Legal Status</th>
                  <th className="px-2 py-1 text-left font-semibold text-slate-600">Expiry</th>
                  <th className="px-2 py-1 text-left font-semibold text-slate-600">Claims</th>
                </tr>
              </thead>
              <tbody>
                {finalPatents.map((p, i) => (
                  <React.Fragment key={p.patent_id + i}>
                    <tr className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                      <td className="px-2 py-1 font-mono text-slate-700">{p.patent_id}</td>
                      <td className="px-2 py-1 text-slate-600">{p.country}</td>
                      <td className="px-2 py-1">
                        <LegalStatusBadge status={p.legal_status} />
                      </td>
                      <td className="px-2 py-1 text-slate-500">{p.expiry_date || '—'}</td>
                      <td className="px-2 py-1">
                        {p.has_claims_snapshot
                          ? (
                            <button
                              onClick={() => handleToggleClaims(p.patent_id)}
                              className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-medium hover:bg-green-200"
                            >
                              {expandedClaimsId === p.patent_id ? t('cfa.fto.claimsHide', 'hide') : t('cfa.fto.claimsShow', 'retrieved ▾')}
                            </button>
                          )
                          : <ClaimsStatusBadge status={p.claims_status} source={p.claims_source} />
                        }
                      </td>
                    </tr>
                    {expandedClaimsId === p.patent_id && claimsTexts[p.patent_id] && (
                      <tr>
                        <td colSpan={5} className="px-3 py-2 bg-slate-50 border-t border-slate-200">
                          <pre className="text-[10px] text-slate-700 whitespace-pre-wrap font-mono max-h-60 overflow-y-auto leading-4">
                            {claimsTexts[p.patent_id]}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FtoScopePane;
